import type { Issue } from "contracts/api/issue/responses";
import {
  type IssueSeverity,
  type IssueStatus,
  OPEN_ISSUE_STATUSES,
} from "contracts/enums/core";
import { ISSUE_STATUS_LABELS } from "contracts/enums/core-labels";
import type { Pillar } from "contracts/enums/metrics";
import { METRIC_CATALOG, type MetricKey } from "contracts/report/catalog";

export const SEVERITY_ORDER = {
  critical: 0,
  high: 1,
  medium: 2,
  low: 3,
} as const satisfies Record<IssueSeverity, number>;

export function isOpen(issue: Issue): boolean {
  return (OPEN_ISSUE_STATUSES as readonly IssueStatus[]).includes(issue.status);
}

// The wording is canonical, not this screen's copy: the issue workbook the
// backend exports has to name these states identically, so the map lives in
// contracts and both processes read it.
export function statusLabel(status: Issue["status"]): string {
  return ISSUE_STATUS_LABELS[status];
}

// Human labels for the derived lifecycle axis. Keyed by the union so a new
// state forces a label here.
const LIFECYCLE_LABELS: Record<Issue["lifecycleState"], string> = {
  new: "Newly detected",
  reopened: "Returned after previous fix",
  still_open: "Still open",
  verified_fixed: "Verified fixed after change",
  not_reverified: "Not reverified",
  legacy: "Legacy",
};

export function lifecycleLabel(state: Issue["lifecycleState"]): string {
  return LIFECYCLE_LABELS[state];
}

export function metricLabel(key: string): string {
  const metric = METRIC_CATALOG.find((metric) => metric.key === key);
  if (!metric) {
    throw new Error(`Unknown metric key: ${key}`);
  }
  return metric.label;
}

// Compact affected-pages label: first path plus a +N overflow count.
export function pagesSummary(pages: Issue["pages"]): string {
  const first = pages[0];
  if (!first) {
    return "—";
  }
  return pages.length > 1 ? `${first.path} +${pages.length - 1}` : first.path;
}

export function pillarOf(key: MetricKey): Pillar {
  const metric = METRIC_CATALOG.find((metric) => metric.key === key);
  if (!metric) {
    throw new Error(`Unknown metric key: ${key}`);
  }
  return metric.pillar;
}

// Default ranking: unresolved first, then by severity, then by the legacy
// priority weight. Stable enough to keep the eye anchored as statuses change.
export function rankBySeverity(issues: Issue[]): Issue[] {
  return [...issues].sort((left, right) => {
    if (isOpen(left) !== isOpen(right)) {
      return isOpen(left) ? -1 : 1;
    }
    const severity =
      SEVERITY_ORDER[left.severity] - SEVERITY_ORDER[right.severity];
    return severity !== 0 ? severity : right.impact - left.impact;
  });
}
